import { isStrengthHistoryCategory } from '../components/activities/workoutExerciseHistoryCategory';

export const SECONDS_PER_REP_EQUIVALENT = 3.0;
export const LB_REPS_PER_KCAL = 167.0;
export const STATIC_SECONDS_PER_REP_DEFAULT = 20.0;
export const STATIC_DURATION_EXPONENT_DEFAULT = 0.75;
export const STATIC_VOLUME_SCALE_DEFAULT = 1.0;

function parseNumeric(value) {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value === 'object') {
    if (typeof value.toNumber === 'function') {
      const numeric = value.toNumber();
      return Number.isFinite(numeric) ? numeric : null;
    }
    if (typeof value.valueOf === 'function') {
      const primitive = value.valueOf();
      if (typeof primitive === 'number' && Number.isFinite(primitive)) {
        return primitive;
      }
    }
    if (typeof value.toString === 'function') {
      const parsed = parseFloat(value.toString());
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }
  return null;
}

function parseDuration(value) {
  if (value === null || value === undefined) {
    return 0;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return 0;
    // Heuristic: values >= 1000 are assumed to be milliseconds.
    return value >= 1000 ? (value / 1000) : value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 0;

    if (trimmed.includes(':')) {
      const parts = trimmed.split(':').map((part) => parseFloat(part) || 0);
      while (parts.length < 3) {
        parts.unshift(0);
      }
      const [hours, minutes, seconds] = parts;
      return (hours * 3600) + (minutes * 60) + seconds;
    }
    
    const parsed = parseFloat(trimmed);
    if (!Number.isFinite(parsed)) return 0;
    if (trimmed.toLowerCase().endsWith('ms')) {
      return parsed / 1000;
    }
    return parsed >= 1000 ? (parsed / 1000) : parsed;
  }

  if (typeof value === 'object') {
    const h = parseFloat(value.hours) || 0;
    const m = parseFloat(value.minutes) || 0;
    const s = parseFloat(value.seconds) || 0;
    return (h * 3600) + (m * 60) + s;
  }

  return 0;
}

function getMetaValue(meta, key, defaultValue) {
  if (!meta) return defaultValue;
  const val = parseNumeric(meta[key]);
  return val !== null ? val : defaultValue;
}

export function calculateEffectiveLoad(set, exerciseMeta, bodyweight) {
  const weight = parseNumeric(set.weight) || 0;
  const bodyWeightLoadFactor = getMetaValue(exerciseMeta, 'bodyWeightLoadFactor', 0);
  const externalLoadFactor = getMetaValue(exerciseMeta, 'externalLoadFactor', 1);
  const baseLoad = getMetaValue(exerciseMeta, 'baseLoad', 0);

  // doubleWeightForVolume: user files weight of one dumbbell/kettlebell but holds
  // one in each hand, so the actual external load is doubled.
  const doubleWeight = exerciseMeta && exerciseMeta.doubleWeightForVolume ? 2 : 1;
  
  // effectiveLoad = max(0, baseLoad + (bodyWeightLoadFactor * bodyweightAtWorkoutDate) + (externalLoadFactor * ext))
  // baseLoad represents inherent equipment resistance (e.g., battle rope weight)
  const load = baseLoad + (bodyWeightLoadFactor * bodyweight) + (externalLoadFactor * weight * doubleWeight);
  return Math.max(0, load);
}

export function findWarmupIndex(sets = [], exerciseMeta = {}, bodyweight = 0) {
  if (!Array.isArray(sets) || sets.length === 0) {
    return null;
  }

  // Calculate effective loads for all sets
  const effectiveLoads = sets.map(set => calculateEffectiveLoad(set, exerciseMeta, bodyweight));
  
  const firstLoad = effectiveLoads[0];
  
  // Filter for subsequent sets that have load > 0
  const otherLoads = effectiveLoads.slice(1).filter(l => l > 0);
  
  // If no subsequent sets have load (e.g. cardio), skip warmup detection
  if (otherLoads.length === 0) {
    return null;
  }

  // If first set has 0 or negative load, but subsequent sets have load, treat as warmup
  if (firstLoad <= 0) {
    return 0;
  }

  // Check if first set load is <= 80% of every subsequent loaded set
  const isWarmup = otherLoads.every((value) => firstLoad <= value * 0.8);
  return isWarmup ? 0 : null;
}

// Trail's mixed walking/running entry has no gait or segment grades. Retain
// its 6-MET reference baseline and add ONLY cumulative positive ascent energy.
// ACSM running vertical VO2 term: 0.9 * speed(m/min) * grade, integrated
// over time = 0.9 * gain(m) ml/kg. At ~5 kcal/L O2 this is 0.0045 kcal/kg/m.
// Use the lower running coefficient (walking is 1.8), not a guessed gait.
// This is a conservative approximation, not a full trail/terrain model.
// See docs/trail-elevation-calories.md for sources and limitations.
function trailAscentCalories(set, bodyweightKg) {
  const raw = set.elevation_gain;
  const gain = typeof raw === 'number' ? raw
    : (typeof raw === 'string' && raw.trim() ? Number(raw) : 0);
  // Same canonical-meter bound as workout-set API validation; corrupt legacy
  // values cannot subtract calories, create Infinity, or earn ascent credit.
  return Number.isFinite(gain) && gain >= 0 && gain <= 100000
    ? 0.0045 * bodyweightKg * gain : 0;
}

export function estimateSetCalories(set, exerciseMeta = {}, bodyweightLbs = 0) {
  const existing = parseNumeric(set?.calories);
  if (existing !== null && existing > 0) return existing;

  const category = String(exerciseMeta.category || 'strength').toLowerCase();
  const bodyweightKg = (bodyweightLbs || 160) * 0.453592;
  const durationSec = parseDuration(set?.duration) || 0;
  const durationHrs = durationSec / 3600;

  if (category === 'cardio') {
    if (exerciseMeta.calorieModel === 'trail-ascent') {
      const weight = bodyweightLbs == null || bodyweightLbs === '' || bodyweightLbs === 0
        ? 160 : Number(bodyweightLbs);
      if (!Number.isFinite(weight) || weight <= 0 || !Number.isFinite(durationSec)) return 0;
    }
    return durationSec > 0 ? 6.0 * bodyweightKg * durationHrs
      + (exerciseMeta.calorieModel === 'trail-ascent' ? trailAscentCalories(set, bodyweightKg) : 0) : 0;
  }
  if (category === 'stretching') {
    return durationSec > 0 ? 2.5 * bodyweightKg * durationHrs : 0;
  }
  if (category === 'sport') {
    return durationSec > 0 ? 5.0 * bodyweightKg * durationHrs : 0;
  }

  const effectiveLoad = calculateEffectiveLoad(set || {}, exerciseMeta, bodyweightLbs);
  const reps = parseNumeric(set?.repetitions) || 0;
  if (category === 'static') {
    const secondsPerRep = getMetaValue(exerciseMeta, 'staticSecondsPerRep', STATIC_SECONDS_PER_REP_DEFAULT);
    const repEquivalent = durationSec > 0 ? durationSec / secondsPerRep : 0;
    const volume = effectiveLoad * repEquivalent;
    return volume > 0 ? volume / LB_REPS_PER_KCAL : 0;
  }

  const doubleReps = exerciseMeta?.doubleRepsForVolume ? 2 : 1;
  const volume = effectiveLoad * reps * doubleReps;
  return volume > 0 ? volume / LB_REPS_PER_KCAL : 0;
}

function resolveMetabolicCalories(set, exerciseMeta = {}, bodyweight = 0) {
  const explicit = parseNumeric(set?.calories);
  const allowsEstimatedCalories = exerciseMeta?.estimateCaloriesForVolume === true;
  const estimated = allowsEstimatedCalories ? parseNumeric(set?.estimated_calories) : null;
  let calories = explicit !== null && explicit > 0
    ? explicit
    : (estimated !== null && estimated > 0 ? estimated : 0);

  if (calories <= 0 && allowsEstimatedCalories) {
    calories = estimateSetCalories(set || {}, exerciseMeta, bodyweight);
  }

  const softCap = getMetaValue(exerciseMeta, 'metabolicVolumeSoftCapCalories', 0);
  if (calories > 0 && softCap > 0) {
    return softCap * Math.log1p(calories / softCap);
  }
  return calories;
}

export function calculateVolumeEquivalent(set, exerciseMeta = {}, bodyweight = 0) {
  const effectiveLoad = calculateEffectiveLoad(set, exerciseMeta, bodyweight);
  
  const reps = parseNumeric(set.repetitions) || 0;
  const sec = parseDuration(set.duration) || 0;
  const kcal = resolveMetabolicCalories(set, exerciseMeta, bodyweight);
  
  const category = String(exerciseMeta.category || 'strength').toLowerCase();

  if (category === 'static') {
    const staticSecondsPerRep = getMetaValue(exerciseMeta, 'staticSecondsPerRep', STATIC_SECONDS_PER_REP_DEFAULT);
    const staticDurationExponent = getMetaValue(exerciseMeta, 'staticDurationExponent', STATIC_DURATION_EXPONENT_DEFAULT);
    const staticVolumeScale = getMetaValue(exerciseMeta, 'staticVolumeScale', STATIC_VOLUME_SCALE_DEFAULT);
    const modalityEfficiencyFactor = getMetaValue(exerciseMeta, 'modalityEfficiencyFactor', 1);

    const secondsPerRep = staticSecondsPerRep > 0 ? staticSecondsPerRep : STATIC_SECONDS_PER_REP_DEFAULT;
    const exponent = staticDurationExponent > 0 ? staticDurationExponent : STATIC_DURATION_EXPONENT_DEFAULT;
    const scale = staticVolumeScale > 0 ? staticVolumeScale : STATIC_VOLUME_SCALE_DEFAULT;
    const normalizedTime = sec > 0 ? (sec / secondsPerRep) : 0;
    const repEquivalent = normalizedTime > 0 ? Math.pow(normalizedTime, exponent) : 0;

    return modalityEfficiencyFactor * (effectiveLoad * repEquivalent * scale);
  }

  const staticFactor = getMetaValue(exerciseMeta, 'staticFactor', 0);
  const modalityEfficiencyFactor = getMetaValue(exerciseMeta, 'modalityEfficiencyFactor', 1);

  // doubleRepsForVolume: user files reps per side for unilateral exercises
  // (e.g. lunges), so total reps are doubled.
  const doubleReps = exerciseMeta && exerciseMeta.doubleRepsForVolume ? 2 : 1;
  
  const repEquivalent = (reps * doubleReps) + (staticFactor * sec / SECONDS_PER_REP_EQUIVALENT);
  const mechanicalVolume = effectiveLoad * repEquivalent;
  const metabolicVolume = kcal * LB_REPS_PER_KCAL;
  
  return modalityEfficiencyFactor * (mechanicalVolume + metabolicVolume);
}

export function calculateWorkoutVolume(sets = [], exerciseMeta = {}, bodyweight = 0) {
  if (!Array.isArray(sets) || sets.length === 0) {
    return 0;
  }

  const warmupIndex = findWarmupIndex(sets, exerciseMeta, bodyweight);
  
  return sets.reduce((total, set, index) => {
    if (index === warmupIndex) {
      return total;
    }
    if (!set.complete) {
        return total;
    }
    return total + calculateVolumeEquivalent(set, exerciseMeta, bodyweight);
  }, 0);
}

// Projection helper: includes in-progress sets (ignores the "complete" flag) but still
// skips the detected warmup set so the preview matches the final total shape.
export function calculateProjectedWorkoutVolume(sets = [], exerciseMeta = {}, bodyweight = 0) {
  if (!Array.isArray(sets) || sets.length === 0) {
    return 0;
  }

  const warmupIndex = findWarmupIndex(sets, exerciseMeta, bodyweight);

  return sets.reduce((total, set, index) => {
    if (index === warmupIndex) {
      return total;
    }

    return total + calculateVolumeEquivalent(set || {}, exerciseMeta, bodyweight);
  }, 0);
}

// Score complements volume by emphasizing heavier sets while still rewarding reps.
// Mechanical component for working sets is an Epley-style estimated 1RM
// (load * (1 + reps/30)) multiplied by a load-bias term:
//   effectiveLoad^SCORE_LOAD_EXPONENT * (1 + repEquivalent / SCORE_EPLEY_DIVISOR)
// Tuned so that 8 reps at 1.05w scores the same as 12 reps at w, encouraging
// users to push the weight up once they can hit ~12 quality reps.
export const SCORE_LOAD_EXPONENT = 2.05;
export const SCORE_EPLEY_DIVISOR = 30;

export function calculateScoreEquivalent(set, exerciseMeta = {}, bodyweight = 0) {
  const effectiveLoad = calculateEffectiveLoad(set, exerciseMeta, bodyweight);

  const reps = parseNumeric(set.repetitions) || 0;
  const sec = parseDuration(set.duration) || 0;
  const kcal = resolveMetabolicCalories(set, exerciseMeta, bodyweight);

  const category = String(exerciseMeta.category || 'strength').toLowerCase();

  if (category === 'static') {
    const staticSecondsPerRep = getMetaValue(exerciseMeta, 'staticSecondsPerRep', STATIC_SECONDS_PER_REP_DEFAULT);
    const staticDurationExponent = getMetaValue(exerciseMeta, 'staticDurationExponent', STATIC_DURATION_EXPONENT_DEFAULT);
    const staticVolumeScale = getMetaValue(exerciseMeta, 'staticVolumeScale', STATIC_VOLUME_SCALE_DEFAULT);
    const modalityEfficiencyFactor = getMetaValue(exerciseMeta, 'modalityEfficiencyFactor', 1);

    const secondsPerRep = staticSecondsPerRep > 0 ? staticSecondsPerRep : STATIC_SECONDS_PER_REP_DEFAULT;
    const exponent = staticDurationExponent > 0 ? staticDurationExponent : STATIC_DURATION_EXPONENT_DEFAULT;
    const scale = staticVolumeScale > 0 ? staticVolumeScale : STATIC_VOLUME_SCALE_DEFAULT;
    const normalizedTime = sec > 0 ? (sec / secondsPerRep) : 0;
    const repEquivalent = normalizedTime > 0 ? Math.pow(normalizedTime, exponent) : 0;

    const mechanicalScore = (effectiveLoad > 0 && repEquivalent > 0)
      ? Math.pow(effectiveLoad, SCORE_LOAD_EXPONENT) * (1 + repEquivalent / SCORE_EPLEY_DIVISOR)
      : 0;

    return modalityEfficiencyFactor * (mechanicalScore * scale);
  }

  const staticFactor = getMetaValue(exerciseMeta, 'staticFactor', 0);
  const modalityEfficiencyFactor = getMetaValue(exerciseMeta, 'modalityEfficiencyFactor', 1);

  const doubleReps = exerciseMeta && exerciseMeta.doubleRepsForVolume ? 2 : 1;

  const repEquivalent = (reps * doubleReps) + (staticFactor * sec / SECONDS_PER_REP_EQUIVALENT);
  const mechanicalScore = (effectiveLoad > 0 && repEquivalent > 0)
    ? Math.pow(effectiveLoad, SCORE_LOAD_EXPONENT) * (1 + repEquivalent / SCORE_EPLEY_DIVISOR)
    : 0;
  // Keep metabolic contribution at the same scale as volume so cardio/sport
  // sets without weight still produce a meaningful score.
  const metabolicScore = kcal * LB_REPS_PER_KCAL;

  return modalityEfficiencyFactor * (mechanicalScore + metabolicScore);
}

export function calculateWorkoutScore(sets = [], exerciseMeta = {}, bodyweight = 0) {
  if (!Array.isArray(sets) || sets.length === 0) {
    return 0;
  }

  const warmupIndex = findWarmupIndex(sets, exerciseMeta, bodyweight);

  return sets.reduce((total, set, index) => {
    if (index === warmupIndex) {
      return total;
    }
    if (!set.complete) {
      return total;
    }
    return total + calculateScoreEquivalent(set, exerciseMeta, bodyweight);
  }, 0);
}

export function calculateProjectedWorkoutScore(sets = [], exerciseMeta = {}, bodyweight = 0) {
  if (!Array.isArray(sets) || sets.length === 0) {
    return 0;
  }

  const warmupIndex = findWarmupIndex(sets, exerciseMeta, bodyweight);

  return sets.reduce((total, set, index) => {
    if (index === warmupIndex) {
      return total;
    }

    return total + calculateScoreEquivalent(set || {}, exerciseMeta, bodyweight);
  }, 0);
}

/**
 * Determines whether the given workout's session for an exercise represents a
 * "personal record" (volume-record / score-record) given the exercise's
 * historical data points returned by GET /workout_exercises/:id/history.
 *
 * Returns false when there's no prior datapoint (baseline session) so we don't
 * award a record on the very first occurrence of the exercise.
 */
export function isVolumeRecordOccurrence(history, category, workout) {
  if (!Array.isArray(history)) return false;
  const normalized = String(category || '').trim().toLowerCase();
  const usesVolume = isStrengthHistoryCategory(normalized) || normalized === 'sport' || normalized === 'static';
  if (!usesVolume) return false;

  const selectScore = (row) => {
    if (!row) return null;
    if (normalized === 'sport') {
      const eq = typeof row.totalEquivalentScore === 'number' ? row.totalEquivalentScore : null;
      return Number.isFinite(eq) && eq > 0 ? eq : null;
    }
    const total = typeof row.totalScore === 'number' ? row.totalScore : null;
    return Number.isFinite(total) && total > 0 ? total : null;
  };

  const points = history
    .map((row) => {
      const score = selectScore(row);
      if (score === null) return null;
      const t = row?.workoutDate ? new Date(row.workoutDate).getTime() : NaN;
      if (!Number.isFinite(t)) return null;
      return {
        t,
        workoutId: row?.workoutId ? String(row.workoutId) : null,
        score
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.t === b.t) return (a.workoutId || '').localeCompare(b.workoutId || '');
      return a.t - b.t;
    });

  if (!points.length) return false;

  const targetId = workout?._id ? String(workout._id) : null;
  let idx = points.findIndex((p) => targetId && p.workoutId === targetId);
  if (idx === -1) {
    const dateMs = workout?.created_at ? new Date(workout.created_at).getTime() : NaN;
    if (Number.isFinite(dateMs)) {
      const tolerance = 1000 * 60 * 60 * 12;
      idx = points.findIndex((p) => Math.abs(p.t - dateMs) < tolerance);
    }
  }
  if (idx <= 0) return false; // not found, or baseline session

  const target = points[idx];
  if (!(Number.isFinite(target.score) && target.score > 0)) return false;

  let prevBest = -Infinity;
  for (let i = 0; i < idx; i += 1) {
    const v = points[i].score;
    if (Number.isFinite(v) && v > prevBest) prevBest = v;
  }
  if (!Number.isFinite(prevBest)) return false;
  const EPSILON = 1e-6;
  return target.score > prevBest + EPSILON;
}
